import { ApiError, useArchiveRecipe, useCommitRecipe, useRecipe, useRecipes } from '@ctcells/api'
import type { Recipe, RecipePayload } from '@ctcells/contract'
import { batchNewPath, dataSourceFor, PATHS } from '@ctcells/core'
import { errorMessage } from '@ctcells/i18n'
import { useNavigate, useParams } from 'react-router'

import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { RecipeDetailScreen } from './RecipeDetailScreen.tsx'
import { toRecipeListRow } from './recipeModel.ts'
import { withCurrentVocabulary } from './recipeVocabulary.ts'
import { RecipeNewScreen, type RecipeCreateRequest } from './RecipeNewScreen.tsx'
import type { ResourceTabId } from '../resources/resourceTabs.ts'
import type { BatchOwners } from '../batches/batchOwnerColumn.tsx'

import { RecipesScreen } from './RecipesScreen.tsx'

/**
 * 레시피 — 목록 · 상세 · 신규 컨테이너.
 *
 * 분류: **2. mock 으로 진행**(`dataSourceFor('recipes') === 'demo'`).
 * 경로는 살아 있고 13건이 실제로 온다. 그런데 **13건 전부 `committed` 라 `draft` 가 0건**이고,
 * 레시피 화면의 핵심 흐름(작성 중 → 확정)을 실물로 한 번도 볼 수 없다.
 * → 🔴 **live 배선은 그대로 붙여 뒀다.** `draft` 가 하나 생기면 `.env` 한 줄
 *   (`VITE_DATA_SOURCE=recipes=live`)로 전환된다. 코드는 안 고친다.
 *
 * ⚠ 목록이 **`payload` 전문을 통째로** 실어 온다 — 13건에 401KB(실측). 목록 화면은 본문을
 *   쓰지 않는다(스텝 수만 센다). 서버 축약(`fields=`)을 요청할 자리다.
 *
 * 🔴 **보관이 붙었다**(2026-09-20). `POST /recipes/{id}/archive` 는 `reason` 이 필수이고
 *    그 문장이 감사 원장에 **그대로** 봉인된다 — 상세 화면의 확인 대화가 **사람에게 받는다.**
 *    프론트가 채워 보내면 감사 기록이 거짓이 된다.
 */

/** 서버 최대 500 아래. ⚠ 항목당 58KB 라 상한을 키우면 응답이 수십 MB 가 된다 */
const PAGE_LIMIT = 100

/**
 * @param resourceTab 열린 무리 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다.
 *   **안 넘기면 무리 탭 줄을 안 그린다**(`useGroupTabLayout` 주석). 장비 관점 콘솔이 그렇게 쓴다.
 */
export interface RecipesContainerProps {
  resourceTab?: ResourceTabId | undefined
  /** 오른쪽 판에 열린 레시피 — 그 행을 «고른 행»으로 그린다 */
  selectedRecipeId?: string | undefined
  /**
   * 「작업자」 이름표 — 🔴 **통합 콘솔 라우트가 `useBatchOwners()` 로 넘긴다**(admin 에게만 값이 있다 · eric 2026-09-30).
   * 안 넘기면(장비 관점 콘솔 · operator · viewer) 「작업자」 열이 없고 `GET /users` 도 안 나간다.
   */
  owners?: BatchOwners | undefined
}

export function RecipesContainer(props: RecipesContainerProps = {}) {
  return dataSourceFor('recipes') === 'demo' ? (
    <RecipesDemo {...props} />
  ) : (
    <RecipesLive {...props} />
  )
}

function RecipesDemo({ resourceTab, selectedRecipeId, owners }: RecipesContainerProps) {
  const access = useScreenAccess()
  return (
    <RecipesScreen
      {...(owners === undefined ? {} : { owners })}
      access={access}
      selectedRecipeId={selectedRecipeId}
      {...(resourceTab === undefined ? {} : { resourceTab })}
    />
  )
}

function RecipesLive({ resourceTab, selectedRecipeId, owners }: RecipesContainerProps) {
  const access = useScreenAccess()
  const recipes = useRecipes({ limit: PAGE_LIMIT })

  if (recipes.isError) return <ScreenState text={errorMessage(recipes.error)} tone="error" />

  return (
    <RecipesScreen
      {...(resourceTab === undefined ? {} : { resourceTab })}
      {...(owners === undefined ? {} : { owners })}
      access={access}
      loading={recipes.isPending}
      recipes={(recipes.data?.items ?? []).map(toRecipeListRow)}
      selectedRecipeId={selectedRecipeId}
    />
  )
}

export interface RecipeDetailContainerProps {
  /** 서랍(목록 오른쪽 판)으로 열렸을 때의 닫기. 없으면 전체 화면이고 「← 레시피」 줄이다 */
  onClose?: (() => void) | undefined
  /** 「작업자」 이름표 — 목록과 같다(admin 에게만). 없으면 요약 카드에 「작업자」 줄이 없다 */
  owners?: BatchOwners | undefined
}

export function RecipeDetailContainer(props: RecipeDetailContainerProps = {}) {
  return dataSourceFor('recipes') === 'demo' ? (
    <RecipeDetailDemo {...props} />
  ) : (
    <RecipeDetailLive {...props} />
  )
}

function RecipeDetailDemo({ onClose, owners }: RecipeDetailContainerProps) {
  const access = useScreenAccess()
  const navigate = useNavigate()
  /*
   * 🔴 **목 갈래에도 붙인다.** 다른 콜백과 달리 이것은 **서버로 나가는 요청이 아니다** —
   *    새 작업 화면으로 가는 이동뿐이라 목 데이터에서도 막을 까닭이 없다.
   */
  return (
    <RecipeDetailScreen
      {...(owners === undefined ? {} : { owners })}
      access={access}
      onClose={onClose}
      onMakeBatch={makeBatchNav(navigate)}
    />
  )
}

/**
 * 「이 레시피로 작업 만들기」 → 새 작업 화면(레시피 미리 골라 둠).
 * 🔴 **이동이 전부다.** 서버 쓰기는 다음 화면의 「작업 만들기」가 한다 — 여기서 `POST` 가 나가면
 *    사람이 고를 기회 없이 작업이 서 버린다.
 */
function makeBatchNav(navigate: ReturnType<typeof useNavigate>) {
  return (recipeId: string) => void navigate(batchNewPath(recipeId))
}

/**
 * 상세는 한 건만 받아 배열로 넘긴다(화면이 `useParams()` 로 고른다).
 *
 * 🔴 **검증·드라이런을 부르지 않는다**(eric 2026-09-29 「검증, 드라이런 이거 빼」). 상세 화면에서
 *    버튼과 결과 카드를 걷었다. `useValidateRecipe` · `useSimulateRecipe` 는 `packages/api` 에 남아 있다.
 * 🔴 보관 성공 뒤 **화면이 상태를 앞지르지 않는다.** `createCommand` 가 무효화만 하고
 *    `status` 는 다시 받은 `Recipe` 에서 온다(낙관적 갱신 없음).
 */
function RecipeDetailLive({ onClose, owners }: RecipeDetailContainerProps) {
  const access = useScreenAccess()
  const navigate = useNavigate()
  const { recipeId } = useParams()
  const recipe = useRecipe({ recipeId })
  const archive = useArchiveRecipe()

  if (recipe.isPending) return <ScreenLoading />
  if (recipe.isError) return <ScreenState text={errorMessage(recipe.error)} tone="error" />

  return (
    <RecipeDetailScreen
      {...(owners === undefined ? {} : { owners })}
      access={access}
      archiving={archive.isPending}
      onClose={onClose}
      recipes={[recipe.data]}
      {...(archive.error === null ? {} : { archiveError: errorMessage(archive.error) })}
      onArchive={(id, reason) => {
        archive.mutate({ recipeId: id, reason })
      }}
      onMakeBatch={makeBatchNav(navigate)}
    />
  )
}

export function RecipeNewContainer() {
  return dataSourceFor('recipes') === 'demo' ? <RecipeNewDemo /> : <RecipeNewLive />
}

function RecipeNewDemo() {
  const access = useScreenAccess()
  return <RecipeNewScreen access={access} />
}

/**
 * 신규 — `POST /recipes` 까지 실제로 나간다(2026-09-20).
 *
 * 🔴 **만드는 방식은 셋이다**(2026-09-29 「값 직접 설정」 되살림).
 *    | 값 직접 설정  | 편집기가 지은 본문을 그대로 올린다(`recipeDraft.buildRecipePayload`)     |
 *    | JSON 가져오기 | 읽어 통과한 본문을 `payload` 로 올린다                                 |
 *    | 기존 복제     | 기반 레시피의 `payload` 를 올린다 — 새 id·v1 은 서버가 정한다          |
 *    🔴 JSON·복제는 **은퇴 스텝 이름만** 새 이름으로 갈아 올린다(`withCurrentVocabulary` · 2026-09-27).
 *       옛 `centrifuge.pick` 이 든 본문은 시작 직후 `paused` 로 멎었다 — 그 외 키는 한 글자도 안 바꾼다.
 *       편집기는 은퇴 이름을 애초에 받지 않는다(불러올 때 갈고, 적어 넣으면 「만들기」를 막는다).
 * 🔴 **서버가 거부하면 그 까닭을 편다**(`violations`). ⚠ v0.9.1 스키마가 조여져 **옛 레시피 17/18건**이
 *    `binds`·`verify_grip`·`verify_released`·`collaborative_6axis` 로 걸린다(2026-09-29 실측) — 그것들을
 *    복제하면 422 이고, 종전에는 「레시피 검증에 실패했습니다」 한 줄뿐이었다.
 *
 * 🔴 **`schema_version` 을 반드시 싣는다.** 계약은 선택이라 적었지만 **실물이 거부한다** —
 *    빈 값이면 422 `REQUEST_VALIDATION_FAIL`(`field: /schema_version`).
 * 🔴 **서버 권위 메타 5개를 화면이 골라 지우지 않는다.** `recipe_validate.compose` 가
 *    `recipe_id`·`version`·`status`·`created_by`·`created_at` 를 **덮어쓴다**(계약의 「있어도 무시」).
 * ⚠ 이름칸이 비면 JSON 문서의 `name`, 복제면 기반 이름이 선다 — 서버 `name` 은 필수라
 *   빈 문자열을 그대로 보내지 않는다.
 */
function RecipeNewLive() {
  const access = useScreenAccess()
  const navigate = useNavigate()
  const recipes = useRecipes({ limit: PAGE_LIMIT })
  const commit = useCommitRecipe()

  if (recipes.isError) return <ScreenState text={errorMessage(recipes.error)} tone="error" />

  const items = recipes.data?.items ?? []

  return (
    <RecipeNewScreen
      access={access}
      // 복제 기반은 **확정·보관된 것만**이다. 그 규칙은 화면이 아니라 여기서 건다
      baseRecipes={items.filter((recipe) => recipe.status !== 'draft').map(toRecipeListRow)}
      committing={commit.isPending}
      {...(commit.error === null ? {} : { commitError: errorMessage(commit.error) })}
      {...(commit.error instanceof ApiError && commit.error.violations !== undefined
        ? { commitViolations: commit.error.violations }
        : {})}
      // 「값 직접 설정」의 출발 틀 — 목록 응답이 본문을 통째로 싣는다(그래서 따로 조회하지 않는다)
      templates={items}
      onCreate={(request) => {
        const vars = commitVarsOf(request, items)
        if (vars === undefined) return
        commit.mutate(vars, {
          onSuccess: (recipe) => void navigate(`${PATHS.recipes}/${recipe.recipe_id}`),
        })
      }}
    />
  )
}

/**
 * 화면이 고른 것 → `POST /recipes` 봉투.
 * 🔴 **재료가 없으면 `undefined` 를 돌려 «보내지 않는다».** 빈 payload 나 지어낸 판 문자열을
 *    올리면 422 가 나고, 그건 사람이 고칠 수 없는 종류의 거절이다.
 */
function commitVarsOf(
  request: RecipeCreateRequest,
  items: readonly Recipe[],
): { name: string; payload: RecipePayload; schemaVersion: string } | undefined {
  const typed = request.name.trim()

  if (request.method === 'json') {
    if (request.payload === undefined || request.schemaVersion === undefined) return undefined
    // ⚠ 문서 안의 이름은 `RecipePayload` 에 선언된 칸이 아니다 — 파서가 따로 꺼내 준다
    const name = typed === '' ? (request.documentName ?? '') : typed
    if (name === '') return undefined
    return {
      name,
      payload: withCurrentVocabulary(request.payload).payload,
      schemaVersion: request.schemaVersion,
    }
  }

  if (request.method === 'clone') {
    const base = items.find((recipe) => recipe.recipe_id === request.baseRecipeId)
    if (base === undefined) return undefined
    return {
      name: typed === '' ? base.name : typed,
      // 🔴 옛 레시피를 복제해도 **새 어휘로만** 만든다 — 은퇴 이름만 갈고 그 외 키는 그대로(`migrateRecipeSteps`)
      payload: withCurrentVocabulary(base.payload).payload,
      schemaVersion: base.schema_version,
    }
  }

  // 'form' — 편집기가 지은 본문을 **그대로** 올린다. 🔴 이름은 빌려 올 곳이 없다 — 화면이 빈 이름을 막는다
  if (request.payload === undefined || request.schemaVersion === undefined || typed === '') {
    return undefined
  }
  return { name: typed, payload: request.payload, schemaVersion: request.schemaVersion }
}
